import {
  initialCustomizeFormValues,
  type CustomizeFormValues,
} from './customize-form-schemas'

// ============================================================
// Agency contact lives inside the legacy `customize` document —
// header/footer.agencyDetails — which is where {{agency.*}} design
// tokens resolve from (customizeAgencyContact in @rh/shared). The
// profile page and the setup wizard both write it through here.
// ============================================================

/**
 * Per-field patch for the agency's saved contact details.
 * - key with a non-empty string → set the value, show it
 * - key with null (or blank)   → clear: show=false, stored value kept
 *   (the preferences endpoint deep-merges, so an absent stored key would
 *   resurrect the old value — hiding is the reliable "cleared" state)
 * - key absent                 → leave the field untouched
 */
export type AgencyContactPatch = Partial<
  Record<'phone' | 'url' | 'email' | 'linkedIn', string | null>
>

type AgencyDetails = CustomizeFormValues['header']['agencyDetails']
type Section = CustomizeFormValues['header']

const CONTACT_KEYS: Record<
  keyof AgencyContactPatch,
  {
    value: 'phoneNumber' | 'url' | 'emailAddress' | 'linkedIn'
    show: keyof AgencyDetails
  }
> = {
  phone: { value: 'phoneNumber', show: 'showPhoneNumber' },
  url: { value: 'url', show: 'showUrl' },
  email: { value: 'emailAddress', show: 'showEmailAddress' },
  linkedIn: { value: 'linkedIn', show: 'showLinkedIn' },
}

/**
 * Merges a contact patch into the stored customize document, mirroring
 * values onto BOTH header and footer agencyDetails exactly like
 * onboarding's seed does (customizeAgencyContact reads header first,
 * footer fills gaps). Returns a complete document — the preferences
 * schema requires one — built over the stock initial values when the
 * agency has no customize yet.
 */
export function mergeAgencyContact(
  stored: CustomizeFormValues | undefined,
  patch: AgencyContactPatch
): CustomizeFormValues {
  const detailsPatch: Partial<AgencyDetails> = {}
  for (const [key, target] of Object.entries(CONTACT_KEYS) as Array<
    [keyof AgencyContactPatch, (typeof CONTACT_KEYS)[keyof AgencyContactPatch]]
  >) {
    if (!(key in patch)) continue
    const raw = patch[key]
    if (raw === undefined) continue
    const value = raw?.trim()
    if (value) {
      detailsPatch[target.value] = value
      ;(detailsPatch as Record<string, unknown>)[target.show] = true
    } else {
      ;(detailsPatch as Record<string, unknown>)[target.show] = false
    }
  }

  const section = (initial: Section, current?: Section): Section => ({
    ...initial,
    ...(current ?? {}),
    // An empty-string agencyName fails the server's min(1); absent is the
    // valid way to spell "none".
    agencyName: (current ?? initial).agencyName?.trim() || undefined,
    agencyDetails: {
      ...initial.agencyDetails,
      ...(current?.agencyDetails ?? {}),
      ...detailsPatch,
    },
  })

  return {
    ...initialCustomizeFormValues,
    ...(stored ?? {}),
    header: section(initialCustomizeFormValues.header, stored?.header),
    footer: section(initialCustomizeFormValues.footer, stored?.footer),
  }
}
